import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { motion, useScroll, useTransform, Variants } from 'framer-motion';
import { RoutePath } from '../types';
import { VEHICLES } from '../constants';

const fadeInUp: Variants = {
  hidden: { opacity: 0, y: 60 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
  }
};

const staggerContainer: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2
    }
  }
};

const lineReveal: Variants = {
  hidden: { scaleX: 0, originX: 0 },
  visible: { 
    scaleX: 1,
    transition: { duration: 1.5, ease: [0.22, 1, 0.36, 1] }
  }
};

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 1000], [0, 400]);
  const opacityHero = useTransform(scrollY, [0, 500], [1, 0]);

  return (
    <div className="bg-black text-white selection:bg-gold-500 selection:text-black">
      
      {/* --- HERO --- */}
      <section className="relative h-screen flex flex-col justify-end pb-20 px-6 lg:px-12 overflow-hidden">
        <motion.div style={{ y: y1, opacity: opacityHero }} className="absolute inset-0 z-0">
          <img 
            src="/limohass-v-class.webp" 
            alt="Mercedes Classe V Limousine Lausanne" 
            className="w-full h-full object-cover opacity-90"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/1 to-transparent"></div>
        </motion.div>

        <div className="relative z-10 max-w-7xl w-full mx-auto">
           <motion.div 
             initial="hidden"
             animate="visible"
             variants={staggerContainer}
           >
              {/* SEO Optimized H1 */}
              <motion.div 
                variants={fadeInUp} 
                className="font-serif text-[12vw] leading-[0.8] tracking-tighter text-white mix-blend-difference mb-8"
              >
                LIMOUSINE 
              </motion.div>
              
              <motion.div variants={lineReveal} className="w-full h-px bg-white/20 mb-8 origin-left" />

              <div className="flex flex-col md:flex-row items-start md:items-end justify-between pt-4">
                 <motion.div variants={fadeInUp} className="max-w-xl">
                    
                    {/* 2. SEO : Le vrai H1 (C'est ici que Google va regarder) */}
                    <h1 className="text-xl md:text-2xl font-light text-slate-300 leading-tight">
                       <span className="block mb-1">
                         Votre service de <strong className="font-medium text-white">Limousine & Chauffeur Privé</strong> à Lausanne.
                       </span>
                       
                       {/* Sous-titre visuel (inclus dans le H1 pour renforcer les mots-clés géographiques) */}
                       <span className="text-white/80 text-lg">
                          Transfert Aéroport Genève • Longue Distance • Alpes
                       </span>
                    </h1>

                 </motion.div>
              
                 <motion.div variants={fadeInUp} className="mt-8 md:mt-0 flex flex-col sm:flex-row gap-4 sm:gap-6 w-full md:w-auto">
                    <button 
                      onClick={() => navigate(RoutePath.BOOKING)}
                      className="bg-white text-black px-8 py-4 font-bold uppercase tracking-widest hover:bg-gold-500 transition-colors duration-300 w-full sm:w-auto"
                    >
                       Réserver une Limousine
                    </button>
                    <button 
                       onClick={() => navigate(RoutePath.FLEET)}
                       className="border border-white/30 text-white px-8 py-4 font-bold uppercase tracking-widest hover:bg-white hover:text-black transition-colors duration-300 w-full sm:w-auto"
                    >
                       Voir la Flotte
                    </button>
                 </motion.div>
              </div>
           </motion.div>
        </div>
      </section>

      {/* --- INTRODUCTION --- */}
      <section className="py-24 md:py-32 px-6 lg:px-12 relative">
         <motion.div 
           variants={lineReveal} 
           initial="hidden" 
           whileInView="visible" 
           viewport={{ once: true }} 
           className="absolute top-0 left-0 w-full h-px bg-white/10" 
         />
         
         <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20">
            <motion.div
               initial="hidden"
               whileInView="visible"
               viewport={{ once: true, margin: "-10%" }}
               variants={fadeInUp}
            >
               <span className="text-gold-500 font-mono text-xs tracking-widest mb-4 block">01 / L'EXCELLENCE SUISSE</span>
               <h2 className="font-serif text-4xl md:text-6xl leading-tight">
                  Service Limousine <br/> <span className="text-slate-500">& Grande Remise.</span>
               </h2>
            </motion.div>
            <motion.div 
               className="flex flex-col justify-end"
               initial="hidden"
               whileInView="visible"
               viewport={{ once: true, margin: "-10%" }}
               variants={fadeInUp}
               transition={{ delay: 0.2 }}
            >
               <p className="text-base md:text-lg text-slate-400 font-light leading-relaxed mb-8">
                  Limohass incarne l'élégance du <strong>service limousine à Lausanne</strong> et Genève. Bien plus qu'un simple transport, nous offrons une expérience de voyage exclusive, digne des plus grands palaces. Que ce soit pour un transfert aéroport, un roadshow financier ou un événement privé, nos <strong>chauffeurs de maître</strong> garantissent une discrétion absolue.
               </p>
               <a href="/a-propos" className="flex items-center gap-2 text-white hover:text-gold-500 transition-colors uppercase tracking-widest text-sm font-bold group">
                  L'Expérience Limohass <ArrowRight size={16} className="group-hover:translate-x-2 transition-transform" />
               </a>
            </motion.div>
         </div>
      </section>

      {/* --- FLEET SHOWCASE --- */}
      <section className="relative">
         <motion.div 
           variants={lineReveal} 
           initial="hidden" 
           whileInView="visible" 
           viewport={{ once: true }} 
           className="absolute top-0 left-0 w-full h-px bg-white/10 z-10" 
         />
         <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-white/10">
            {VEHICLES.map((vehicle, index) => (
               <motion.div 
                  key={vehicle.id} 
                  className="group relative h-[500px] md:h-[600px] overflow-hidden bg-[#050505]"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2, duration: 0.8 }}
               >
                  <div className="absolute inset-0 overflow-hidden">
                    <motion.img 
                       src={vehicle.image} 
                       alt={`Location ${vehicle.name} limousine lausanne`} 
                       className="w-full h-full object-cover opacity-70 group-hover:opacity-100 transition-opacity duration-700 grayscale group-hover:grayscale-0"
                       whileHover={{ scale: 1.05 }}
                       transition={{ duration: 0.7 }}
                    />
                  </div>
                  <div className="absolute inset-0 p-8 md:p-12 flex flex-col justify-between z-10 pointer-events-none">
                     <div className="flex justify-between items-start">
                        <motion.span 
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.3 + index * 0.1 }}
                          className="text-xs font-mono border border-white/20 px-2 py-1 text-slate-300"
                        >
                          {vehicle.category}
                        </motion.span>
                        <ArrowUpRight className="text-white opacity-0 group-hover:opacity-100 transition-opacity transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                     </div>
                     <div>
                        <motion.h3 
                          initial={{ y: 20, opacity: 0 }}
                          whileInView={{ y: 0, opacity: 1 }}
                          transition={{ delay: 0.4 + index * 0.1 }}
                          className="font-serif text-3xl text-white mb-2"
                        >
                          {vehicle.name}
                        </motion.h3>
                        <p className="text-sm text-white opacity-0 group-hover:opacity-100 transition-opacity duration-500 translate-y-4 group-hover:translate-y-0">
                          {vehicle.description.substring(0, 100)}...
                        </p>
                     </div>
                  </div>
                  <button 
                    onClick={() => navigate(RoutePath.FLEET)}
                    className="absolute inset-0 w-full h-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-gold-500"
                    aria-label={`Voir les détails de ${vehicle.name}`}
                  ></button>
               </motion.div>
            ))}
         </div>
      </section>

      {/* --- SERVICES LIST --- */}
      <section className="py-24 md:py-32 px-6 lg:px-12 bg-[#020202] relative">
         <motion.div variants={lineReveal} initial="hidden" whileInView="visible" viewport={{ once: true }} className="absolute bottom-0 left-0 w-full h-px bg-white/10" />
         
         <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-end mb-16 md:mb-20">
               <motion.div 
                 initial="hidden"
                 whileInView="visible"
                 viewport={{ once: true }}
                 variants={fadeInUp}
                 className="mb-8 md:mb-0"
               >
                  <span className="text-gold-500 font-mono text-xs tracking-widest mb-4 block">02 / PRESTATIONS HAUT DE GAMME</span>
                  <h2 className="font-serif text-4xl md:text-5xl">Nos Services Limousine</h2>
               </motion.div>
               <button onClick={() => navigate(RoutePath.SERVICES)} className="text-slate-400 hover:text-white transition-colors border-b border-transparent hover:border-white pb-1">
                 Voir tous les services
               </button>
            </div>

            <motion.div 
              className="grid grid-cols-1 border-t border-white/10"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-10%" }}
              variants={staggerContainer}
            >
               {[
                  { title: "Transfert Aéroport Genève", desc: "Connexion VIP vers Lausanne, Montreux, Gstaad depuis GVA.", num: "01" },
                  { title: "Mise à Disposition", desc: "Chauffeur de maître à l'heure pour vos rendez-vous.", num: "02" },
                  { title: "Roadshows Financiers", desc: "Logistique de transport précise pour délégations.", num: "03" },
                  { title: "Service Mariage", desc: "Limousine de luxe avec décoration pour votre grand jour.", num: "04" }
               ].map((svc) => (
                  <motion.div 
                    key={svc.num} 
                    variants={fadeInUp}
                    className="group grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 py-10 border-b border-white/10 items-center hover:bg-white/[0.02] transition-colors cursor-default"
                  >
                     <div className="col-span-1 md:col-span-1 text-slate-600 font-mono text-xl">{svc.num}</div>
                     <div className="col-span-1 md:col-span-5 text-2xl md:text-3xl font-serif text-white group-hover:pl-4 transition-all duration-300">{svc.title}</div>
                     <div className="col-span-1 md:col-span-4 text-slate-400 font-light text-sm md:text-base">{svc.desc}</div>
                     <div className="col-span-1 md:col-span-2 flex justify-end md:justify-end justify-start">
                        <div className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center group-hover:bg-white group-hover:text-black transition-all">
                           <ArrowUpRight size={18} />
                        </div>
                     </div>
                  </motion.div>
               ))}
            </motion.div>
         </div>
      </section>

      {/* --- DESTINATIONS --- */}
      <section className="relative py-24 md:py-32 px-6 lg:px-12 overflow-hidden">
         <div className="max-w-7xl mx-auto mb-16">
            <motion.div
               initial="hidden"
               whileInView="visible"
               viewport={{ once: true }}
               variants={fadeInUp}
            >
              <span className="text-gold-500 font-mono text-xs tracking-widest mb-4 block">03 / DESTINATIONS PHARES</span>
              <h2 className="font-serif text-4xl md:text-5xl">Suisse & Alpes</h2>
              <p className="mt-4 text-slate-400 font-light max-w-2xl">
                 Service de limousine vers toute la Suisse Romande et les stations de ski prestigieuses.
              </p>
            </motion.div>
         </div>
         
         <motion.div 
            className="flex overflow-x-auto gap-1 pb-8 no-scrollbar"
            initial={{ x: 100, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: "easeOut" }}
         >
            {[
               { name: "Genève", keyword: "Transfert Genève", img: "/ville-geneva.webp" },
               { name: "Lausanne", keyword: "Limousine Lausanne", img: "/ville-lausanne.webp" },
               { name: "Montreux", keyword: "Riviera Limousine", img: "/ville-montreux.webp" },
               { name: "Gstaad", keyword: "Ski Gstaad", img: "/ski-Gstaad.webp" },
               { name: "Zermatt", keyword: "Alpes Suisses", img: "/ville-Zermatt.webp" }
            ].map((dest, i) => (
               <motion.div 
                  key={i} 
                  className="min-w-[280px] md:min-w-[400px] h-[400px] md:h-[500px] relative group overflow-hidden bg-dark-800"
                  whileHover={{ width: 420 }} // Subtle expand effect
                  transition={{ duration: 0.4 }}
               >
                  <img src={dest.img} alt={`Service Limousine ${dest.name}`} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 scale-100 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-80"></div>
                  <div className="absolute bottom-8 left-8">
                     <h3 className="font-serif text-2xl md:text-3xl text-white">{dest.name}</h3>
                     <p className="text-gold-500 text-xs uppercase tracking-widest mt-1 opacity-0 group-hover:opacity-100 transition-opacity">{dest.keyword}</p>
                     <div className="h-[1px] w-0 bg-gold-500 group-hover:w-12 transition-all duration-500 mt-2"></div>
                  </div>
               </motion.div>
            ))}
         </motion.div>
      </section>

      {/* --- CTA --- */}
      <section className="py-24 md:py-40 px-6 lg:px-12 bg-white text-black text-center relative overflow-hidden">
         <motion.div 
           initial={{ scaleY: 0 }}
           whileInView={{ scaleY: 1 }}
           viewport={{ once: true }}
           transition={{ duration: 0.8 }}
           className="absolute top-0 left-0 w-full h-full bg-slate-50 -z-10 origin-bottom"
         />
         
         <div className="max-w-4xl mx-auto">
            <motion.h2 
               initial={{ y: 50, opacity: 0 }}
               whileInView={{ y: 0, opacity: 1 }}
               viewport={{ once: true }}
               transition={{ duration: 0.8 }}
               className="font-serif text-4xl md:text-7xl mb-8"
            >
               Votre Chauffeur Attend.
            </motion.h2>
            <motion.p 
               initial={{ y: 50, opacity: 0 }}
               whileInView={{ y: 0, opacity: 1 }}
               viewport={{ once: true }}
               transition={{ delay: 0.2, duration: 0.8 }}
               className="text-lg md:text-xl text-gray-600 mb-12 font-light"
            >
               Réservez votre transfert aéroport ou votre mise à disposition en quelques clics. Confirmation immédiate.
            </motion.p>
            <motion.button 
               whileHover={{ scale: 1.05 }}
               whileTap={{ scale: 0.95 }}
               onClick={() => navigate(RoutePath.BOOKING)}
               className="bg-black text-white px-12 py-5 font-bold uppercase tracking-widest hover:bg-gold-500 hover:text-black transition-all shadow-2xl w-full md:w-auto"
            >
               Demander un Devis
            </motion.button>
         </div>
      </section>

    </div>
  );
};

export default Home;